import React, { useState, useEffect } from 'react';
import { parse } from 'csv-parse/browser/esm';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { CSVPreviewProps } from '@/types/chat';

export const CSVPreview = ({ files, csvMetadata }: CSVPreviewProps) => {
  const [csvData, setCsvData] = useState<Record<string, any>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Use backend-provided metadata if available
    if (csvMetadata?.headers && csvMetadata?.sample_rows) {
      const { headers: metadataHeaders } = csvMetadata;
      setHeaders(metadataHeaders);
      setTotalRows(csvMetadata.row_count || 0);

      const records = csvMetadata.sample_rows.map(row => {
        const record: Record<string, any> = {};
        metadataHeaders.forEach((header, index) => {
          record[header] = row[index] || '';
        });
        return record;
      });

      setCsvData(records);
      setLoading(false);
      return;
    }

    if (files.length === 0) {
      setLoading(false);
      return;
    }

    const file = files[0];
    const reader = new FileReader();

    reader.onload = async e => {
      const text = e.target?.result as string;

      try {
        parse(
          text,
          {
            columns: true,
            skip_empty_lines: true,
            trim: true,
          },
          (err, records: Record<string, any>[]) => {
            if (err) {
              setError('Failed to parse CSV file');
              console.error(err);
              return;
            }

            if (records.length > 0) {
              setHeaders(Object.keys(records[0]));
              setTotalRows(records.length);
              setCsvData(records.slice(0, 4));
            }
            setLoading(false);
          },
        );
      } catch (err) {
        setError('Error reading CSV file');
        console.error(err);
        setLoading(false);
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
      setLoading(false);
    };

    reader.readAsText(file);
  }, [files, csvMetadata]);

  if (loading) return <div>Loading CSV...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div
      className={`border rounded-lg overflow-hidden bg-white shadow-sm ${
        isExpanded ? 'max-w-[100%]' : 'w-fit'
      }`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="px-4 py-3 hover:bg-slate-50 transition-colors text-left">
        <div className="flex items-center gap-2 whitespace-nowrap">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-slate-500 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-500 flex-shrink-0" />
          )}
          <span className="text-sm font-medium text-slate-700">
            {csvMetadata?.filename || files[0]?.name}
          </span>
          <span className="text-xs text-slate-500 flex-shrink-0">
            ({totalRows} rows total)
          </span>
        </div>
        {!isExpanded && (
          <div className="text-xs text-slate-500 mt-1 ml-6">
            Click to preview first {csvData.length} rows
          </div>
        )}
      </button>

      {isExpanded && (
        <div className="border-t relative">
          <div className="overflow-auto max-h-64 pb-4">
            <table className="border-collapse w-full">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  {headers.map((header, i) => (
                    <th
                      key={i}
                      className="px-3 py-2 text-left text-xs font-medium text-slate-700 whitespace-nowrap border-r border-slate-200 last:border-r-0 bg-slate-50">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {csvData.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-slate-200 hover:bg-slate-50">
                    {headers.map((header, j) => (
                      <td
                        key={j}
                        className="px-3 py-2 text-sm text-slate-600 whitespace-nowrap border-r border-slate-100 last:border-r-0">
                        {row[header]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
